import { Dialog } from 'primereact/dialog';
import React, { type FC } from 'react';
import { useDecisionFormContext } from '../../contexts/DecisionMaking/DecisionFormContext';
import { usePropertyContext } from '../../contexts/DecisionMaking/PropertyContext';
import { Toast } from 'primereact/toast';
import QuickNetSheetTable from './quick-net-sheet/QuickNetSheetTable';
import {
  formatFloat,
  getPropertyAddress,
} from '../../utility/utility_functions';
import { IconX } from '../icons/OurIcons';
import { useToast } from '../../contexts/ToastContext';
import { useDecisionSubmission } from '../../hooks/useDecisionMakingSubmission';
import { useListSortContext } from '../../contexts/DecisionMaking/ListSortContext';
import { useApproachContext } from '../../contexts/DecisionMaking/ApproachContext';

export interface ConfirmDecisionModalProps {
  isOpen: boolean;
  setModalOpen: (value: boolean) => void;
  onSuccess: (value: boolean) => void;
}

const ConfirmDecisionModal: FC<ConfirmDecisionModalProps> = ({
  isOpen,
  setModalOpen,
  onSuccess,
}) => {
  const toast = useToast();

  const { state, dispatch } = useDecisionFormContext();

  const { submitDecision } = useDecisionSubmission();

  const { activeListSortId } = useListSortContext();

  const {
    fixedCosts,
    estimatedValue,
    estimatedValuePerSqft,
    maxBid,
    maxBidPerSqft,
    estimatedProfit,
    roi,
  } = state;

  const { propertyId: activePropertyId, propertyDetails } =
    usePropertyContext();

  const { selectedApproach } = useApproachContext();

  const selectedApproachName = selectedApproach?.approach?.name;
  const saleCostPercent = selectedApproach?.approach?.sale_cost_percent;

  if (!activePropertyId || !propertyDetails) {
    return null;
  }

  const onSubmitDecision = async () => {
    const result = await submitDecision(
      state,
      activeListSortId,
      activePropertyId
    );
    if (!result) {
      onSuccess(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong. Please contact support.',
      });
    } else {
      onSuccess(true);
      dispatch({ type: 'RESET_FORM_UPDATED' });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Decision submitted successfully.',
      });
    }
    setModalOpen(false);
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        id="confirm-decision-modal"
        pt={{
          header: {
            className: 'hidden',
          },
          mask: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          },
        }}
        header="Confirm Decision"
        visible={isOpen}
        modal
        className="p-0 w-[100vw] lg:w-[50vw] bg-white rounded"
        onHide={() => {
          if (!isOpen) return;
          setModalOpen(false);
        }}
      >
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-indigo-600">
              Future Sale Quick Net Sheet
            </h3>
            <button
              onClick={() => setModalOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <IconX />
            </button>
          </div>

          <div className="mb-6">
            <p
              id="confirm-decision-modal-address"
              className="text-lg font-semibold text-center text-gray-800 mb-4"
            >
              {getPropertyAddress(propertyDetails)}
            </p>

            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">
                  Approach Name:
                </span>
                <span
                  id="confirm-decision-modal-approach-name"
                  className="ml-2 text-gray-900"
                >
                  {selectedApproachName}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Sales Cost:</span>
                <span className="ml-2 text-gray-900">
                  {saleCostPercent != null
                    ? `${formatFloat(saleCostPercent * 100, 2)}%`
                    : '-'}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 justify-center w-full">
            <QuickNetSheetTable
              fixedCosts={selectedApproach ? selectedApproach.fixedCosts : fixedCosts}
              estimatedValue={estimatedValue}
              maxBid={maxBid}
              sqFt={propertyDetails.sqft}
              estimatedProfit={estimatedProfit}
              estimatedValuePerSqft={estimatedValuePerSqft}
              maxBidPerSqft={maxBidPerSqft}
              roi={roi}
              closingCosts={selectedApproach?.closingCosts}
              proratedTaxes={selectedApproach?.proratedTaxes}
            />
          </div>

          <div className="flex justify-center mt-8">
            <button
              id="confirm-decision-modal-submit-button"
              type="button"
              onClick={onSubmitDecision}
              className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 transition-colors"
            >
              Submit
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ConfirmDecisionModal;
